import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Calendar, MapPin, Ruler, User, ChevronRight, Search, ToggleLeft, ToggleRight, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { formatCurrency, PROFIL_LABELS, getRegies } from "@/utils/simulateurUtils";

const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";
const PROG_C = { "174": "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400", mpr: "bg-blue-500/20 text-blue-600 dark:text-blue-400", MPR: "bg-blue-500/20 text-blue-600 dark:text-blue-400", cumule: "bg-violet-500/20 text-violet-600 dark:text-violet-400" };
const PROG_L = { "174": "174", mpr: "MPR", MPR: "MPR", cumule: "Op. Cumulée" };
const STATUS_L = { rdv_admin: "RDV Admin", en_cours: "En cours", valide: "Validé", termine: "Terminé", a_traiter: "À traiter" };
const STATUS_C = { rdv_admin: "bg-blue-500/20 text-blue-600 dark:text-blue-400", en_cours: "bg-amber-500/20 text-amber-600 dark:text-amber-400", valide: "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400", termine: "bg-muted/50 text-gray-700", a_traiter: "bg-orange-500/20 text-orange-600 dark:text-orange-400" };

function SortHeader({ label, field, sortField, sortDir, onSort }) {
  const active = sortField === field;
  return (
    <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors"
      onClick={() => onSort(field)}>
      <div className="flex items-center gap-1">
        {label}
        {active ? (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
      </div>
    </th>
  );
}

export default function DossiersRegie() {
  const navigate = useNavigate();
  const [etudesItems, setEtudesItems] = useState([]);
  const [dossierItems, setDossierItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterProg, setFilterProg] = useState("all");
  const [viewMode, setViewMode] = useState("dossier");
  const [programmeTab, setProgrammeTab] = useState("all");
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const source = localStorage.getItem("label_energie_user") || "regie";

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const etudesParams = viewMode === "dossier" ? `doc_type=dossier` : `saved_only=true`;
        const etudesRes = await fetch(`${API}/etudes?source=${encodeURIComponent(source)}&${etudesParams}`);
        const etudesData = await etudesRes.json();
        setEtudesItems(viewMode === "etude" ? etudesData.filter(d => d.type !== "dossier") : etudesData);

        if (viewMode === "dossier") {
          const dossRes = await fetch(`${API}/dossiers`);
          const allDoss = await dossRes.json();
          setDossierItems(allDoss.filter(d => d.assigned_to === source || d.created_by === source));
        } else {
          setDossierItems([]);
        }
      } catch (err) { setEtudesItems([]); setDossierItems([]); }
      setLoading(false);
    };
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [source, viewMode]);

  const allItems = useMemo(() => {
    if (viewMode === "etude") return etudesItems;
    const etudeIds = new Set(etudesItems.map(e => e.id));
    const converted = dossierItems.filter(d => !etudeIds.has(d.id)).map(d => ({
      id: d.id, type: "dossier", created_at: d.created_at,
      beneficiaire: { nom: d.client_name || "", telephone: d.client_phone || "" },
      profil: d.profile || "", surface: d.metrage || 0, zone: d.zone || "",
      recommendation: d.programme || "", status: d.statut_dossier || "",
      is_sci_multi: d.is_sci_multi || false,
      quote_amount: d.quote_amount || 0,
      total_aides: d.total_aides || 0,
      precarite: d.precarite || "",
      metrage: d.metrage || 0,
      steps: d.steps || [],
    }));
    return [...etudesItems, ...converted];
  }, [etudesItems, dossierItems, viewMode]);

  const sorted = useMemo(() => {
    const filtered = allItems.filter(d => {
      if (search) {
        const s = search.toLowerCase();
        if (!(d.beneficiaire?.nom || "").toLowerCase().includes(s) && !(d.id || "").toLowerCase().includes(s)) return false;
      }
      if (filterStatus !== "all" && d.status !== filterStatus) return false;
      if (filterProg !== "all" && d.recommendation !== filterProg) return false;
      if (programmeTab !== "all") {
        const rec = (d.recommendation || "").toLowerCase();
        if (programmeTab === "mpr" && rec !== "mpr") return false;
        if (programmeTab === "174" && rec !== "174") return false;
        if (programmeTab === "cumule" && rec !== "cumule" && rec !== "cumulée") return false;
      }
      return true;
    });
    return [...filtered].sort((a, b) => {
      let va, vb;
      switch (sortField) {
        case "nom": va = (a.beneficiaire?.nom || "").toLowerCase(); vb = (b.beneficiaire?.nom || "").toLowerCase(); break;
        case "profil": va = a.profil || ""; vb = b.profil || ""; break;
        case "surface": va = parseFloat(a.surface) || 0; vb = parseFloat(b.surface) || 0; break;
        case "zone": va = a.zone || ""; vb = b.zone || ""; break;
        case "programme": va = a.recommendation || ""; vb = b.recommendation || ""; break;
        case "status": va = a.status || ""; vb = b.status || ""; break;
        case "commission": {
          const getComm = (d) => {
            const p = (d.recommendation || d.programme || "").toLowerCase();
            let r = d.regies;
            if (!r || Object.keys(r).length === 0) r = getRegies("regie", ((d.precarite || "bleu").toLowerCase() === "jaune" ? "jaune" : "bleu"), d.surface || 80);
            if (p === "cumule" || p === "cumulée") return (r.r174Cum || 0) + (r.rMPRCum || 0);
            if (p === "174") return r.r174 || 0;
            if (p === "mpr") return r.rMPR || 0;
            return 0;
          };
          va = getComm(a); vb = getComm(b); break;
        }
        case "created_at": va = a.created_at || ""; vb = b.created_at || ""; break;
        default: va = a.id || ""; vb = b.id || "";
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [allItems, search, filterStatus, filterProg, programmeTab, sortField, sortDir]);

  const isDossierMode = viewMode === "dossier";

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="dossiers-regie-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">{isDossierMode ? "Mes Dossiers" : "Mes Études"}</h1>
          <p className="text-sm text-muted-foreground mt-1">{sorted.length} {isDossierMode ? "dossier(s)" : "étude(s)"}</p>
        </div>
        <button onClick={() => setViewMode(v => v === "dossier" ? "etude" : "dossier")}
          className={cn("flex items-center gap-2 px-4 py-2 rounded-xl border font-medium text-sm transition-all",
            isDossierMode ? "bg-indigo-500/10 border-indigo-200 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-500/20" : "bg-orange-500/10 border-orange-500/30 text-orange-700 hover:bg-orange-500/20"
          )} data-testid="switch-dossier-etude">
          {isDossierMode ? <ToggleLeft className="w-5 h-5" /> : <ToggleRight className="w-5 h-5" />}
          {isDossierMode ? "Voir les Études" : "Voir les Dossiers"}
        </button>
      </div>

      {/* Programme tabs */}
      {isDossierMode && (
        <div className="flex gap-1 border-b border-border pb-1">
          {[
            { key: "all", label: "Tous" },
            { key: "mpr", label: "MPR" },
            { key: "174", label: "174" },
            { key: "cumule", label: "Op. Cumulée" },
          ].map(tab => (
            <button key={tab.key} onClick={() => setProgrammeTab(tab.key)}
              className={cn("px-4 py-2 text-sm font-medium rounded-t-lg transition-colors",
                programmeTab === tab.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
              )} data-testid={`tab-prog-${tab.key}`}>
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher par nom ou ID..." className="pl-9 rounded-xl" data-testid="search-dossiers" />
        </div>
        {isDossierMode && (
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 rounded-xl border border-border bg-background text-sm" data-testid="filter-status">
            <option value="all">Tous les statuts</option>
            <option value="rdv_admin">RDV Admin</option>
            <option value="en_cours">En cours</option>
            <option value="valide">Validé</option>
            <option value="termine">Terminé</option>
          </select>
        )}
        <select value={filterProg} onChange={e => setFilterProg(e.target.value)} className="px-3 py-2 rounded-xl border border-border bg-background text-sm" data-testid="filter-programme">
          <option value="all">Tous les programmes</option>
          <option value="174">174</option>
          <option value="mpr">MPR</option>
          <option value="cumule">Cumulée</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Chargement...</div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-border rounded-xl">
          <FileText className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-muted-foreground">{allItems.length === 0 ? (isDossierMode ? "Aucun dossier" : "Aucune étude") : "Aucun résultat"}</p>
        </div>
      ) : isDossierMode ? (
        <div className="card-floating overflow-hidden overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-border bg-muted/30">
                <SortHeader label="ID" field="id" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Nom bénéficiaire" field="nom" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground">Tél.</th>
                <SortHeader label="Catégorie" field="profil" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Surface" field="surface" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Zone" field="zone" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Programme" field="programme" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Statut" field="status" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Commission" field="commission" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Date" field="created_at" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              </tr>
            </thead>
            <tbody>
              {sorted.map(d => {
                // Compute commission for this dossier
                const prog = (d.recommendation || d.programme || "").toLowerCase();
                let comm = 0;
                // SCI multi: use user's commission_sci % or default 10%
                if (d.is_sci_multi && (d.quote_amount || d.total_aides)) {
                  const ca = d.quote_amount || d.total_aides || 0;
                  const userCommStr = localStorage.getItem("label_energie_commissions");
                  let sciPct = 10;
                  if (userCommStr) { try { sciPct = JSON.parse(userCommStr).commission_sci || 10; } catch (err) {} }
                  comm = Math.round(ca / 1.055 * sciPct / 100);
                } else {
                  let regies = d.regies;
                  if (!regies || Object.keys(regies).length === 0) {
                    const surf = d.surface || d.metrage || 80;
                    const prec = (d.precarite || "bleu").toLowerCase();
                    regies = getRegies("regie", prec === "jaune" ? "jaune" : "bleu", surf);
                  }
                  if (prog === "cumule" || prog === "cumulée") comm = (regies.r174Cum || 0) + (regies.rMPRCum || 0);
                  else if (prog === "174") comm = regies.r174 || 0;
                  else if (prog === "mpr") comm = regies.rMPR || 0;
                }
                return (
                <tr key={d.id} className="border-b border-border/40 hover:bg-muted/20 cursor-pointer transition-colors"
                  onClick={() => navigate(`/dossier-regie/${d.id}`)} data-testid={`dossier-row-${d.id}`}>
                  <td className="px-3 py-2.5 text-xs font-mono text-muted-foreground">#{d.id?.slice(-6)}</td>
                  <td className="px-3 py-2.5 text-sm font-semibold">{d.beneficiaire?.nom || "-"}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{d.beneficiaire?.telephone || "-"}</td>
                  <td className="px-3 py-2.5"><span className="text-xs px-2 py-0.5 rounded-full bg-muted/50 text-gray-700 font-medium">{PROFIL_LABELS?.[d.profil] || d.profil || "-"}</span></td>
                  <td className="px-3 py-2.5 text-xs">{d.surface || 0} m²</td>
                  <td className="px-3 py-2.5 text-xs">{d.zone || "-"}</td>
                  <td className="px-3 py-2.5"><span className={cn("px-2 py-0.5 rounded-full text-[10px] font-bold uppercase", PROG_C[d.recommendation] || "bg-muted/50 text-gray-600")}>{PROG_L[d.recommendation] || d.recommendation || "-"}</span></td>
                  <td className="px-3 py-2.5"><span className={cn("px-2 py-0.5 rounded-full text-[10px] font-bold uppercase", STATUS_C[d.status] || "bg-muted/50 text-gray-600")}>{STATUS_L[d.status] || d.status || "-"}</span></td>
                  <td className="px-3 py-2.5 text-xs font-bold text-[hsl(var(--renova-green))]">{comm > 0 ? formatCurrency(comm) : "—"}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{fmtDate(d.created_at)}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="space-y-3">
          {sorted.map(d => (
            <div key={d.id} className="border rounded-xl bg-card p-4 hover:shadow-md transition-all cursor-pointer group border-border hover:border-orange-300"
              onClick={() => navigate(`/etude/${d.id}`)} data-testid={`etude-${d.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-orange-500/20"><FileText className="w-5 h-5 text-orange-600" /></div>
                  <div><p className="font-semibold text-sm">{d.beneficiaire?.nom || "Sans nom"}</p><p className="text-xs text-muted-foreground">#{d.id?.slice(-6)}</p></div>
                </div>
                <div className="flex items-center gap-3">
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtDate(d.created_at)}</p>
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-orange-500" />
                </div>
              </div>
              <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><User className="w-3 h-3" />{PROFIL_LABELS?.[d.profil] || d.profil}</span>
                <span className="flex items-center gap-1"><Ruler className="w-3 h-3" />{d.surface} m²</span>
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{d.zone}</span>
                {d.recommendation && <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-bold uppercase", PROG_C[d.recommendation] || "bg-muted/50")}>{PROG_L[d.recommendation] || d.recommendation}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
